import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getEnv } from '@unni/platform'

import { GlobalPageProps } from './_app'

import { getDefaultMeta } from '@/shared/utils'
import HomeTemplate from '@/components/atomic/templates/HomeTemplate'
import TopDownloadScreen from '@/components/atomic/templates/TopDownloadScreen'
import { addCriteoEvent, postAmplitude, postGA } from '@/core/lib'
import { CommunityDocumentService } from '@/features/community/api/document'
import { type User } from '@/features/member/member.type'
import { JsonLd as RichSearchResultJsonLd } from '@/components'
import { getOrganizationJsonLdWithContext } from '@/utils/scheme'
import ResponsiveLayout from '@/components/atomic/templates/ResponsiveLayout'
import { AirbridgeEvents, sendAirbridgeEvent } from '@/utils/airbridge'
import useAppDownloadModal from '@/hooks/Modal/AppDownloadModal/useAppDownloadModal'
import ContextEnhancer from '@/shared/libs/context-enhancer'
import { checkAndMarkUtmUser, showAppDownloadBannerInJP } from '@/features/shared/utils/jp-ads-banner-control'
import { getServerTranslation } from '@/utils/i18n/i18n-server'
import { Locales } from '@/utils/language'
import { TreatmentCategoryFilterService } from '@/features/treatment-category-filter/api/treatment-category-filter.service'
import { createSolarApiClient } from '@/shared/libs/api-client'
import { TreatmentCategory } from '@/features/treatment-category-filter/types/treatment-filter.type'
import { useLocales } from '@/shared/hooks/useLocales'

import type { Organization, WithContext } from 'schema-dts'
import type { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next'
import type { SimpleMedicalTip } from '@/components/atomic/organisms/MedicalTipWidget'

interface HomePageProps extends GlobalPageProps {
  user: User
  treatments: TreatmentCategory[]
  medicalTips: SimpleMedicalTip[]
  organizationJsonLd: WithContext<Organization> | null
  locale: Locales
}

const HomePage: NextPage<HomePageProps> = ({
  user,
  treatments,
  medicalTips,
  organizationJsonLd,
  locale,
}: HomePageProps) => {
  const { isLocale } = useLocales()
  const router = useRouter()
  const commonAmplitudePropertiesRef = useRef<{
    path: string
    referrer: string
  } | null>(null)

  const [isOpenSearch, setIsOpenSearch] = useState(false)

  const handleClickColumn = (id: string) => {
    postAmplitude('click_column_in_main', {
      ...(commonAmplitudePropertiesRef.current || {}),
      post_id: id,
    })

    router.push(`/posts/${id}`)
  }

  const handleClickColumnMore = () => {
    postAmplitude('click_column_more_in_main', {
      ...(commonAmplitudePropertiesRef.current || {}),
    })

    router.push(`/posts`)
  }
  const handleClickMedicalTip = (id: number) => {
    postAmplitude('click_meditip_in_main', {
      ...(commonAmplitudePropertiesRef.current || {}),
      tip_id: id,
    })
  }

  const handleClickMedicalTipMore = () => {
    postAmplitude('click_meditip_more_in_main', {
      ...(commonAmplitudePropertiesRef.current || {}),
    })
  }
  useEffect(() => {
    if (user && user.id !== 0) {
      sessionStorage.setItem('user_id', user.id.toString())
    } else {
      sessionStorage.removeItem('user_id')
    }
  }, [user])

  useEffect(() => {
    const defaultProperties = {
      path: `${location.pathname}${location.search}`,
      referrer: document.referrer,
    }

    commonAmplitudePropertiesRef.current = defaultProperties
  }, [])

  useEffect(() => {
    postGA('view_main', 'pageview')
  }, [])

  useEffect(() => {
    sendAirbridgeEvent(AirbridgeEvents.HOME_PAGE_VIEW, {
      semanticAttributes: {},
    })
  }, [])

  const hasAddCriteoEventBeenCalled = useRef(false)

  useEffect(() => {
    if (!hasAddCriteoEventBeenCalled.current) {
      addCriteoEvent(user?.loginId ?? '', { event: 'viewHome' })

      hasAddCriteoEventBeenCalled.current = true
    }
  }, [user?.loginId])

  useEffect(() => {
    postAmplitude('view_main')
  }, [])

  useEffect(() => {
    /**
     * @desc 광고(UTM) 유저 감지 및 저장 (JP 환경 배너 제어용)
     * @context https://healingpaper.slack.com/archives/C027T02M1L6/p1760682709289289
     */
    checkAndMarkUtmUser()
  }, [])

  const hideDownloadBannerCondition = useMemo(() => {
    return isLocale.US || isLocale.TH || !showAppDownloadBannerInJP(!!user?.id, isLocale.JP)
  }, [user?.id, isLocale.JP, isLocale.US, isLocale.TH])

  useAppDownloadModal({
    inactive: hideDownloadBannerCondition,
  })

  return (
    <ResponsiveLayout>
      <TopDownloadScreen
        userProfileImage={user?.profileImage}
        useGnb={true}
        selectedGnbTabKey="home"
        isRootScreen={true}
        isOpenSearch={isOpenSearch}
        isTopDownloadBannerHidden={hideDownloadBannerCondition}
        onChangeOpenSearchState={(isOpenSearch) => {
          setIsOpenSearch(isOpenSearch)
        }}
      >
        <HomeTemplate
          user={user}
          treatments={treatments}
          medicalTips={medicalTips}
          onClickMoreColumn={handleClickColumnMore}
          onClickColumn={handleClickColumn}
          onClickMoreMedicalTip={handleClickMedicalTipMore}
          onClickMedicalTip={handleClickMedicalTip}
          onClickSearchInput={() => {
            postAmplitude('click_search_textbox', {
              ...(commonAmplitudePropertiesRef.current || {}),
            })
            setIsOpenSearch(true)
          }}
          onClickBanner={(tag) => {
            postGA('click_banner', 'click', tag)
            postAmplitude('click_mainbanner_in_main', {
              ...(commonAmplitudePropertiesRef.current || {}),
              banner_tag: tag,
            })
            sendAirbridgeEvent(AirbridgeEvents.HOME_BANNER_CLICK, {
              semanticAttributes: {
                achievementID: tag,
              },
            })
          }}
          onClickTreatment={(treatment) => {
            postGA('click_category', 'click', treatment.name)
            postAmplitude('click_treatment_ctg_in_main', {
              treatment_ctg_id: treatment.id,
              treatment_ctg_name: treatment.name,
              ...(commonAmplitudePropertiesRef.current || {}),
            })
          }}
        />
      </TopDownloadScreen>
      <RichSearchResultJsonLd data={organizationJsonLd} />
    </ResponsiveLayout>
  )
}

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<HomePageProps>> => {
  const contextEnhancer = new ContextEnhancer(ctx)
  const enhancedContext = (await contextEnhancer.activateAuth()).activateLocale().getContext()
  const { t } = getServerTranslation(enhancedContext.locale)
  const meta = getDefaultMeta(enhancedContext.locale, {
    canonical: `${getEnv('NEXT_PUBLIC_HOST')}${enhancedContext.pathnamePrefix}`,
  })
  const apiHeaders = {
    Authorization: enhancedContext.token,
    'Accept-Language': enhancedContext.locale,
    'x-forwarded-for': enhancedContext.userIp,
  }

  // Organization JSON-LD
  const csEmail = t('common.footer.cs.email.address')
  const organizationJsonLd = getOrganizationJsonLdWithContext({
    name: meta.title,
    description: meta.description,
    logo: 'https://www.gangnamunni.com/images/icon/favicon-96x96.png',
    email: csEmail,
    locale: enhancedContext.locale,
  })

  try {
    const treatmentCategoryFilterService = new TreatmentCategoryFilterService(
      createSolarApiClient({ headers: enhancedContext.apiHeaders }),
    )
    const communityDocumentService = new CommunityDocumentService(
      createSolarApiClient({ headers: enhancedContext.apiHeaders }),
    )
    const results = await Promise.allSettled([
      treatmentCategoryFilterService.getTreatmentCategoryFilter(),
      communityDocumentService.getMedicalTipsSolar(0, apiHeaders),
    ])

    const treatments = results[0].status === 'fulfilled' ? results[0].value.treatmentCategories : []
    const medicalTips = results[1].status === 'fulfilled' ? results[1].value : []

    const simpleMedicalTips: SimpleMedicalTip[] = medicalTips.slice(0, 4).map((tip) => ({
      tipId: tip?.id,
      title: tip?.title ?? '',
      thumbnailUrl: tip?.thumbnails[0] ?? '',
      writerName: tip?.personaName,
    }))

    return {
      props: {
        user: enhancedContext.user,
        treatments,
        medicalTips: simpleMedicalTips,
        organizationJsonLd,
        locale: enhancedContext.locale,
        meta,
      },
    }
  } catch (error) {
    console.error('/ index page error:', error)
    return {
      notFound: true,
    }
  }
}

export default HomePage
